/**
 * `advisor-intake.emailVerification`: the record of WHICH signed-in account sent a snapshot,
 * and whether the address typed into the form is that account's address (2026-10-07).
 *
 * ## Where the value comes from
 *
 * Written by `services/intake-writer` at submit, and only from stored state:
 *
 *   1. the façade asks appcore who the caller is (`GET /me/intake-authority`, with the caller's
 *      own token) and passes appcore's `userId` and `email` to the reader or the writer;
 *   2. the first of those to act binds `(userId, firm)` to a draft in the claims table
 *      (`@steward-app/intake-claims` `bindDraftOwner`), storing the folded and exact hashes of
 *      that address and the time of binding;
 *   3. the writer's save carries them onto the draft row as `claimEmailHash`,
 *      `claimExactEmailHash` and `claimVerifiedAt`, set once, together;
 *   4. the writer's submit builds this object from the STORED draft row.
 *
 * The draft message schemas are `strictObject`s that declare none of these fields, so a payload
 * naming `emailVerification`, `claimVerifiedAt` or `claimExactEmailHash` is rejected at the
 * queue boundary. Pinned in `services/intake-writer/src/__tests__/draft.test.ts`.
 *
 * ## What it establishes, and what it does not
 *
 * `verifiedAt` is when the draft was bound to the account, and the hashes are of the address
 * appcore held for that account at that moment. It is NOT proof that this request, or any
 * request, demonstrated control of the mailbox: that rests on the sign-up flow of the identity
 * provider, which this app does not observe. The owner key everywhere is the user id; the
 * address is carried for the firm to see and for the comparison below.
 *
 * `clientEmailMatches` is EXACT equality (trimmed and lowercased, no `+tag` or dot folding)
 * between the address typed into About You and the account's address. `false` is a reachable
 * honest answer, so a consumer must require `=== true`.
 *
 * The hashes stay server-side: the advisor portal receives {@link IntakeEmailVerificationSummary}
 * via {@link summarizeIntakeEmailVerification}. Absent means "nothing was recorded", never
 * "verified".
 */
import { z } from 'zod';

/** The one method that exists: the draft was owned by a signed-in account with a second check. */
export const INTAKE_EMAIL_VERIFICATION_METHOD = 'signed-in' as const;

export const IntakeEmailVerificationSchema = z.strictObject({
  method: z.literal(INTAKE_EMAIL_VERIFICATION_METHOD),
  /** `sha256(throttleKey(address))` of the account's address, lowercase hex (sub-address and dot folded). */
  emailHash: z.string().regex(/^[0-9a-f]{64}$/),
  /** `sha256(normalizeEmail(address))` of the account's address, lowercase hex (no folding). */
  exactEmailHash: z.string().regex(/^[0-9a-f]{64}$/),
  /** UTC ISO instant the draft was bound to the account. */
  verifiedAt: z.iso.datetime(),
  /** Whether `clientEmail` is exactly (trimmed, lowercased) the account's address. */
  clientEmailMatches: z.boolean(),
  /**
   * The exact hash of the address on the owner binding at the moment of SUBMIT, when the
   * binding's folded hash is the draft row's owner hash; absent otherwise.
   *
   * {@link exactEmailHash} is set once, by the first save. This is read again at submit. A
   * consumer that relies on it must read absent as "not the proof holder" (fail closed). As
   * read on 2026-10-07 nothing in this repository compares the two.
   */
  submitterExactEmailHash: z.string().regex(/^[0-9a-f]{64}$/).optional(),
});

export type IntakeEmailVerification = z.infer<typeof IntakeEmailVerificationSchema>;

/** What the advisor portal is sent: the verification without any hash. See the header. */
export type IntakeEmailVerificationSummary = Omit<
  IntakeEmailVerification, 'emailHash' | 'exactEmailHash' | 'submitterExactEmailHash'
>;

/**
 * Projects a stored `emailVerification` for a browser: the three named fields when the stored
 * value parses, `undefined` otherwise (absent and unparseable both read as unverified). None of
 * the three hashes (`emailHash`, `exactEmailHash`, `submitterExactEmailHash`) is among them.
 *
 * Named fields, never a spread with the hashes deleted, so a field a later writer adds does not
 * reach the portal by default.
 */
export function summarizeIntakeEmailVerification(
  value: unknown,
): IntakeEmailVerificationSummary | undefined {
  const parsed = IntakeEmailVerificationSchema.safeParse(value);
  if (!parsed.success) return undefined;
  const { method, verifiedAt, clientEmailMatches } = parsed.data;
  return { method, verifiedAt, clientEmailMatches };
}
